
import { logger } from "@/services/LoggingService";
import { StorageError } from "./errors";
import { supabase } from "@/integrations/supabase/client";
import { CACHE_SETTINGS } from "./constants";
import { cleanupStorageFolder } from "./file-upload";

const COMPONENT = 'URLUpload';

export async function uploadFromUrl(
  basePath: string,
  fileName: string,
  url: string,
  options: { 
    upsert?: boolean;
    cacheControl?: keyof typeof CACHE_SETTINGS | string;
  } = {}
): Promise<string> {
  try {
    logger.info(COMPONENT, 'Starting URL upload', {
      basePath,
      fileName,
      url
    });

    // If upsert is true, clean up existing files first
    if (options.upsert) {
      await cleanupStorageFolder(basePath);
    }

    const { data, error } = await supabase.functions.invoke('upload-from-url', {
      body: {
        url,
        basePath,
        fileName,
        options: {
          ...options,
          // Generate responsive WebP variants by default using Supabase image renderer
          generateVariants: true,
          variantWidths: [480, 768, 1024],
          quality: 80
        }
      }
    });

    if (error) {
      throw new StorageError(
        'Failed to upload from URL',
        'URL_UPLOAD_ERROR',
        { error }
      );
    }

    if (!data.publicUrl) {
      throw new StorageError(
        'No public URL returned from upload',
        'MISSING_URL',
        { data }
      );
    }

    logger.info(COMPONENT, 'URL upload successful', {
      publicUrl: data.publicUrl,
      contentType: data.contentType,
      size: data.size,
      duration: data.duration
    });

    return data.publicUrl;

  } catch (error: any) {
    logger.error(COMPONENT, 'URL upload operation failed', { 
      error: error.message,
      details: error,
      operation: 'uploadFromUrl'
    });
    
    if (error instanceof StorageError) {
      throw error;
    }
    
    throw new StorageError(
      'Failed to upload from URL',
      'URL_UPLOAD_ERROR',
      { originalError: error }
    );
  }
}
